import React, { useState, useEffect } from 'react';
import {
  CircularProgress,
  Chip,
  IconButton,
  Collapse,
  Box,
  Button,
  Divider
} from '@mui/material';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  UserIcon,
  ClockIcon,
  ShoppingBagIcon,
  XCircleIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import {
  PencilIcon as PencilIconSolid
} from '@heroicons/react/24/solid';
import { format } from 'date-fns';
import api from '../api/api';
import { CURRENCY } from '../config/currency';

interface OrderLineGroup {
  orderLineId: number | null;
  sessionCode: string;
  status: string;
  startedAt?: string;
  endedAt?: string;
  totalAmount?: number;
  paidAmount?: number;
  customerType?: string;
  activeTotal?: number;
  customer?: {
    username: string;
    email?: string;
    phone?: string;
    type: string;
  };
  orders: Order[];
  orderCount: number;
}

interface Order {
  id: number;
  customOrderId: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  total: number;
  orderType?: string;
  deliveryType?: string;
  orderLineId?: number;
  user?: {
    id?: number;
    username: string;
    email?: string;
  };
  isGuestOrder?: boolean;
  items: OrderItem[];
  acceptedAt?: string;
  preparingAt?: string;
  readyAt?: string;
  completedAt?: string;
}

interface OrderItem {
  id: number;
  quantity: number;
  price: number;
  comment?: string;
  menuItemName?: string; // Snapshot of item name stored in order
  portionName?: string;  // Portion name (e.g., SMALL, MEDIUM, LARGE)
  portionId?: number;
  menuItem?: {
    id: number;
    name: string;
    price: number;
    description?: string;
  };
  toppings?: Array<{
    id: number;
    name: string;
  }>;
}

interface TableOrderHistoryProps {
  tableId?: number;
  theme: { palette: { mode: string; primary: { main: string; }; text: { secondary: string; }; background: { paper: string; }; }; };
  activeOnly?: boolean;
  noPadding?: boolean;
  isSteward?: boolean;
  onModifyOrder?: (orderId: number) => void;
  onCancelOrder?: (orderId: number) => void;
  onServeOrder?: (orderId: number) => void;
  cancellingOrderId?: number | null;
  servingOrderId?: number | null;
  /** Persistent inline errors from failed cancel/serve actions, keyed by order id. */
  actionErrors?: Record<number, string>;
  refreshTrigger?: number; // Timestamp or counter to trigger refresh
}

const TableOrderHistory: React.FC<TableOrderHistoryProps> = ({
  tableId,
  theme,
  activeOnly = false,
  noPadding = false,
  isSteward = false,
  onModifyOrder,
  onCancelOrder,
  onServeOrder,
  cancellingOrderId,
  servingOrderId,
  actionErrors,
  refreshTrigger
}) => {
  const [loading, setLoading] = useState(true);
  const [orderLineGroups, setOrderLineGroups] = useState<OrderLineGroup[]>([]);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [expandedOrders, setExpandedOrders] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (tableId) {
      fetchTableOrders();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableId, activeOnly, refreshTrigger]);

  const fetchTableOrders = async () => {
    try {
      setLoading(true);
      console.log(`Fetching orders for table ${tableId}, activeOnly: ${activeOnly}`);

      // Use optimized endpoint for fast performance (minimal joins, no deep item/KOT data)
      let url = `/api/tables/${tableId}/order-summary`;
      if (activeOnly) {
        url += '?activeOnly=true';
      }
      const response = await api.get(url);
      console.log('Table orders response:', response.data);

      // Filter for active OrderLines only if activeOnly is true
      let groups = response.data;
      console.log('📋 All order groups from API:', groups);

      if (activeOnly) {
        console.log('📋 Filtering for active OrderLines. Current statuses:', groups.map((g: OrderLineGroup) => g.status));
        groups = groups.filter((group: OrderLineGroup) => {
          // Filter by OrderLine status (ACTIVE, BILL_REQUESTED, PARTIALLY_PAID, PAID)
          // Exclude CLOSED and CANCELLED OrderLines
          // Note: SERVED, COMPLETED, DELIVERED are ORDER statuses, not OrderLine statuses
          const isActive = group.status !== 'CLOSED' && group.status !== 'CANCELLED';
          console.log(`📋 OrderLine ${group.orderLineId} status: ${group.status}, isActive: ${isActive}`);
          return isActive;
        });
        console.log('📋 Filtered active OrderLines:', groups.length);
      }

      setOrderLineGroups(groups);

      // Expand all groups if activeOnly, otherwise just the first
      if (groups.length > 0) {
        if (activeOnly) {
          // Expand all groups for active orders view
          const allGroupIds = groups.map((g: OrderLineGroup) =>
            (g.orderLineId || 'legacy').toString()
          );
          setExpandedGroups(new Set(allGroupIds));
        } else {
          // Expand only the first group for history view
          const firstGroupId = groups[0].orderLineId || 'legacy';
          setExpandedGroups(new Set([firstGroupId.toString()]));
        }
      }
    } catch (error) {
      console.error('Error fetching table orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleGroup = (groupId: string) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupId)) {
      newExpanded.delete(groupId);
    } else {
      newExpanded.add(groupId);
    }
    setExpandedGroups(newExpanded);
  };

  const toggleOrder = (orderId: number) => {
    const newExpanded = new Set(expandedOrders);
    if (newExpanded.has(orderId)) {
      newExpanded.delete(orderId);
    } else {
      newExpanded.add(orderId);
    }
    setExpandedOrders(newExpanded);
  };

  const getStatusColor = (status: string) => {
    switch (status.toUpperCase()) {
      case 'ACTIVE': return '#059669'; // softer green
      case 'CLOSED': return '#6b7280'; // neutral gray
      case 'CANCELLED': return '#dc2626'; // softer red
      case 'PENDING': return '#d97706'; // softer amber
      case 'ACCEPTED': return '#2563eb'; // softer blue
      case 'PREPARING': return '#2563eb'; // softer blue
      case 'READY': return '#059669'; // softer green
      case 'SERVED': return '#8b5cf6'; // purple - food served, payment pending
      case 'COMPLETED': return '#6b7280'; // neutral gray
      case 'LEGACY': return '#6b7280'; // softer gray
      default: return '#6b7280'; // neutral gray
    }
  };

  const getCustomerIcon = (type?: string) => {
    switch (type) {
      case 'registered':
        return <UserIcon className="h-4 w-4" />;
      case 'guest':
        return <UserIcon className="h-4 w-4" style={{ opacity: 0.6 }} />;
      case 'vendor':
      case 'steward':
        return <ShoppingBagIcon className="h-4 w-4" />;
      default:
        return <UserIcon className="h-4 w-4" />;
    }
  };

  const isActiveOrder = (status: string) => {
    // Include SERVED because served orders are not settled yet
    const activeStatuses = ['PENDING', 'ACCEPTED', 'PREPARING', 'READY', 'SERVED'];
    return activeStatuses.includes(status.toUpperCase());
  };

  const containerClass = noPadding ? "" : "p-6";

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20">
        <CircularProgress style={{ color: theme.primary }} />
      </div>
    );
  }

  // Flatten all orders from all groups into a single array
  const allOrders = orderLineGroups.flatMap(group => group.orders).sort((a, b) =>
    new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );

  if (allOrders.length === 0) {
    return (
      <div className={containerClass}>
        {!noPadding && (
          <h3 className="text-lg font-semibold mb-4" style={{ color: theme.text }}>
            {activeOnly ? 'Active Orders' : 'Order History'}
          </h3>
        )}
        <div className="text-center py-12">
          {activeOnly ? (
            <>
              <ShoppingBagIcon className="h-12 w-12 mx-auto mb-3" style={{ color: theme.gray?.[400] }} />
              <p style={{ color: theme.gray?.[600] }}>No active orders</p>
            </>
          ) : (
            <>
              <ClockIcon className="h-12 w-12 mx-auto mb-3" style={{ color: theme.gray?.[400] }} />
              <p style={{ color: theme.gray?.[600] }}>No orders found for this table</p>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={containerClass}>
      {!noPadding && (
        <h3 className="text-lg font-semibold mb-4" style={{ color: theme.text }}>
          {activeOnly ? 'Active Orders' : 'Order History'}
        </h3>
      )}

      <div className="space-y-4">
        {allOrders.map((order) => {
          const isOrderExpanded = expandedOrders.has(order.id);

          return (
            <div
              key={order.id}
              className="border rounded-lg overflow-hidden"
              style={{
                borderColor: theme.components?.card?.border,
                backgroundColor: theme.background,
                boxShadow: theme.palette?.mode === 'dark'
                  ? '0 1px 2px 0 rgba(0, 0, 0, 0.2)'
                  : '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
              }}
            >
              {/* Order Header */}
              <div
                className="p-3 hover:bg-opacity-80 transition-all"
              >
                <div
                  className="flex items-center justify-between gap-2 cursor-pointer"
                  onClick={() => toggleOrder(order.id)}
                >
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    {isOrderExpanded ? (
                      <ChevronDownIcon
                        className="h-4 w-4 transition-transform duration-200"
                        style={{
                          color: 'var(--text-tertiary)',
                          flexShrink: 0
                        }}
                      />
                    ) : (
                      <ChevronRightIcon
                        className="h-4 w-4 transition-transform duration-200"
                        style={{
                          color: 'var(--text-tertiary)',
                          flexShrink: 0
                        }}
                      />
                    )}

                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold" style={{ color: theme.text }}>
                          Order #{order.customOrderId}
                        </p>
                        {/* Show customer name if available in the order's user object or fallback to finding it in groups */}
                        <span className="text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 truncate max-w-[100px]">
                          {order.user?.username || orderLineGroups.find(g => g.orderLineId === order.orderLineId)?.customer?.username || 'Guest'}
                        </span>
                      </div>
                      <p className="text-sm text-gray-500" style={{ color: theme.gray?.[500] }}>
                        {format(new Date(order.createdAt), 'HH:mm:ss')}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 flex-shrink-0">
                    <div className="text-right">
                      <p className="font-medium text-sm" style={{ color: theme.text }}>
                        {CURRENCY.symbol}{order.total.toFixed(2)}
                      </p>
                    </div>
                    <Chip
                      label={order.status}
                      size="small"
                      style={{
                        backgroundColor: getStatusColor(order.status) + '20',
                        color: getStatusColor(order.status),
                        fontSize: '0.75rem',
                        fontWeight: 500
                      }}
                    />
                  </div>
                </div>

                {/* Steward Action Buttons - Always visible */}
                {isSteward && (order.status === 'PENDING' || isActiveOrder(order.status)) && (
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 1,
                      mt: 1.5,
                      pt: 1.5,
                      borderTop: 1,
                      borderColor: theme.components?.card?.border || '#e2e8f0',
                      flexWrap: 'wrap'
                    }}
                  >
                    {order.status === 'PENDING' && onModifyOrder && (
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<PencilIconSolid style={{ width: 14, height: 14 }} />}
                        onClick={(e) => {
                          e.stopPropagation();
                          onModifyOrder(order.id);
                        }}
                        sx={{
                          backgroundColor: 'var(--action) !important',
                          color: 'white !important',
                          px: 1.5,
                          py: 0.5,
                          fontSize: '0.8rem',
                          fontWeight: 500,
                          textTransform: 'none',
                          borderRadius: '6px',
                          minHeight: '30px',
                          '& .MuiButton-startIcon': {
                            marginRight: 0.5,
                            marginLeft: -0.25
                          },
                          '&:hover': {
                            backgroundColor: 'var(--action) !important'
                          }
                        }}
                      >
                        Modify
                      </Button>
                    )}
                    {order.status === 'PENDING' && onCancelOrder && (
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={cancellingOrderId === order.id
                          ? <CircularProgress size={14} color="inherit" />
                          : <XCircleIcon style={{ width: 14, height: 14 }} />}
                        onClick={(e) => {
                          e.stopPropagation();
                          onCancelOrder(order.id);
                        }}
                        disabled={cancellingOrderId === order.id}
                        aria-busy={cancellingOrderId === order.id || undefined}
                        sx={{
                          backgroundColor: '#6b7280 !important', /* basil-todo */
                          color: 'white !important',
                          px: 1.5,
                          py: 0.5,
                          fontSize: '0.8rem',
                          fontWeight: 500,
                          textTransform: 'none',
                          borderRadius: '6px',
                          minHeight: '44px',
                          '& .MuiButton-startIcon': {
                            marginRight: 0.5,
                            marginLeft: -0.25
                          },
                          '&:hover': {
                            backgroundColor: '#4b5563 !important' /* basil-todo */
                          },
                          '&:disabled': {
                            backgroundColor: '#d1d5db !important', /* basil-todo */
                            color: 'var(--text-tertiary) !important'
                          },
                          '&:focus-visible': {
                            outline: '2px solid var(--focus-ring)',
                            outlineOffset: '2px'
                          }
                        }}
                      >
                        {cancellingOrderId === order.id ? 'Cancelling…' : 'Cancel'}
                      </Button>
                    )}

                    {/* Mark as Served button - for any active order except SERVED */}
                    {isActiveOrder(order.status) && order.status !== 'SERVED' && onServeOrder && (
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={servingOrderId === order.id
                          ? <CircularProgress size={14} color="inherit" />
                          : <CheckCircleIcon style={{ width: 14, height: 14 }} />}
                        onClick={(e) => {
                          e.stopPropagation();
                          console.log('Serve button clicked for order:', order.id);
                          onServeOrder(order.id);
                        }}
                        disabled={servingOrderId === order.id}
                        aria-busy={servingOrderId === order.id || undefined}
                        sx={{
                          backgroundColor: 'var(--action) !important',
                          color: 'white !important',
                          px: 1.5,
                          py: 0.5,
                          fontSize: '0.8rem',
                          fontWeight: 500,
                          textTransform: 'none',
                          borderRadius: '6px',
                          minHeight: '44px',
                          '& .MuiButton-startIcon': {
                            marginRight: 0.5,
                            marginLeft: -0.25
                          },
                          '&:hover': {
                            backgroundColor: 'var(--action) !important'
                          },
                          '&:disabled': {
                            backgroundColor: '#d1d5db !important', /* basil-todo */
                            color: 'var(--text-tertiary) !important'
                          },
                          '&:focus-visible': {
                            outline: '2px solid var(--focus-ring)',
                            outlineOffset: '2px'
                          }
                        }}
                      >
                        {servingOrderId === order.id ? 'Serving…' : 'Served'}
                      </Button>
                    )}
                  </Box>
                )}

                {/* Inline action error — persists next to the row until retry/success */}
                {actionErrors?.[order.id] && (
                  <div
                    role="alert"
                    style={{ color: 'var(--status-bad)', fontSize: '0.75rem', fontWeight: 500, marginTop: 6 }}
                  >
                    {actionErrors[order.id]}
                  </div>
                )}
              </div>

              {/* Order Items */}
              <Collapse in={isOrderExpanded}>
                <Divider style={{ borderColor: theme.components?.card?.border }} />
                <div className="p-3 space-y-2">
                  {order.items.map((item, index) => (
                    <div key={index} className="flex justify-between items-start text-sm gap-2">
                      <div className="flex-1 min-w-0" style={{ color: theme.text }}>
                        <div className="flex items-center gap-1 flex-wrap">
                          <span className="font-medium">{item.quantity}x</span>
                          <span className="break-words">{item.menuItemName || item.menuItem?.name || 'Unknown Item'}</span>
                          {item.portionName && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                              {item.portionName}
                            </span>
                          )}
                        </div>
                        {item.comment && (
                          <div className="text-xs mt-1" style={{ color: theme.gray?.[600] }}>
                            Note: {item.comment}
                          </div>
                        )}
                        {item.toppings && item.toppings.length > 0 && (
                          <div className="text-xs mt-1" style={{ color: theme.gray?.[600] }}>
                            Toppings: {item.toppings.map(t => t.name).join(', ')}
                          </div>
                        )}
                      </div>
                      <div className="flex-shrink-0 text-right">
                        <span className="font-medium" style={{ color: theme.text }}>
                          {CURRENCY.symbol}{(item.quantity * item.price).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  ))}

                  {/* Order Timeline */}
                  <div className="mt-3 pt-3 border-t" style={{ borderColor: theme.components?.card?.border }}>
                    <p className="text-sm font-medium mb-2" style={{ color: theme.text }}>
                      Timeline
                    </p>
                    <div className="space-y-1 text-xs" style={{ color: theme.gray?.[600] }}>
                      <div>Created: {format(new Date(order.createdAt), 'HH:mm:ss')}</div>
                      {order.acceptedAt && (
                        <div>Accepted: {format(new Date(order.acceptedAt), 'HH:mm:ss')}</div>
                      )}
                      {order.preparingAt && (
                        <div>Preparing: {format(new Date(order.preparingAt), 'HH:mm:ss')}</div>
                      )}
                      {order.readyAt && (
                        <div>Ready: {format(new Date(order.readyAt), 'HH:mm:ss')}</div>
                      )}
                      {order.completedAt && (
                        <div>Completed: {format(new Date(order.completedAt), 'HH:mm:ss')}</div>
                      )}
                    </div>
                  </div>
                </div>
              </Collapse>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default TableOrderHistory;